import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { plural } from "@/lib/utils";
import { DetailSkeleton } from "@/components/Skeletons";
import { fmtDate } from "@/lib/dates";
import DocumentDownload from "@/components/DocumentDownload";
import { documentHref } from "@/lib/exportColumns";

export default function EmployeeHistory() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/employees/${id}/history`).then((r) => setD(r.data)); }, [id]);
  if (!d) return <DetailSkeleton testid="employee-history-skeleton" />;
  return (
    <div className="space-y-5" data-testid="employee-history">
      <Button variant="ghost" onClick={() => nav(-1)} className="pl-0"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="overline text-primary">Training record</div>
          <h2 className="font-heading font-bold text-3xl mt-1 break-words">{d.employee.full_name}</h2>
          <div className="text-sm text-muted-foreground mt-1">
            {[d.employee.employee_code, d.employee.designation].filter(Boolean).join(" · ")}
          </div>
          {/* What the printed record states about the person, so a blank is noticed here first. */}
          <div className="text-sm text-muted-foreground mt-0.5" data-testid="employee-profile">
            {[d.employee.qualification, d.employee.previous_experience && `${d.employee.previous_experience} before joining`]
              .filter(Boolean).join(" · ") || "Qualification and previous experience not recorded"}
          </div>
        </div>
        {/* Shakun's Individual Training Record (F-TRG-04), for this person, every delivered training. */}
        <DocumentDownload docKey="individual_record" label="Training record"
                          href={documentHref(`${API}/employees/${id}/training-record`)}
                          testid="employee-record-download" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-5 border border-border"><div className="overline text-muted-foreground">Total hours</div><div className="text-3xl font-heading font-bold mt-2">{d.total_hours}</div><div className="text-xs text-muted-foreground mt-1">Delivered training only</div></Card>
        <Card className="p-5 border border-border"><div className="overline text-muted-foreground">Attended</div><div className="text-3xl font-heading font-bold mt-2">{d.attended}</div></Card>
        <Card className="p-5 border border-border"><div className="overline text-muted-foreground">Attendance %</div><div className="text-3xl font-heading font-bold mt-2">{d.attendance_pct}%</div><div className="text-xs text-muted-foreground mt-1">Of {plural(d.marked ?? 0, "session")} marked{(d.nominated ?? 0) > (d.marked ?? 0) ? ` · ${d.nominated - d.marked} not yet taken` : ""}</div></Card>
      </div>
      {/* The table scrolls inside the card. Six columns laid out at 628px in a 358px card on a
          phone, overflow-hidden clipped them, and the Certificate column vanished entirely,
          taking with it the only mark on this screen that a certificate was revoked. */}
      <Card className="border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-zebra">
            <thead className="bg-muted/70 border-b border-border"><tr className="text-left"><th className="px-4 py-3">Date</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Hours</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Certificate</th></tr></thead>
            <tbody>
              {d.trainings.map((t) => (
                <tr key={t.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2">{fmtDate(t.training_date)}</td>
                  <td className="px-4 py-2 font-medium">{t.title}</td>
                  <td className="px-4 py-2">{t.category}</td>
                  <td className="px-4 py-2">{t.duration_hours}</td>
                  <td className="px-4 py-2">{t.attendance_status}</td>
                  <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">
                    {t.certificate_number ? (
                      t.certificate_revoked_at ? (
                        // The number stays readable: somebody asking what became of a
                        // certificate they were shown deserves an answer, not a blank.
                        <span className="text-muted-foreground">
                          <span className="line-through">{t.certificate_number}</span>
                          <span className="ml-1.5 not-italic text-destructive-text">revoked</span>
                        </span>
                      ) : t.certificate_number
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
